import { describeError } from "./error_message";
import type { QuestionBibleVerse } from "../types/question.types";

export type PendingQuestion = { text: string; verses: QuestionBibleVerse[] };

const KEY = "pendingGuestQuestion";
export const PENDING_QUESTION_TTL_MS = 60 * 60 * 1000;
/** Fired after a save, so a reader who is already signed in gets it sent at once. */
export const PENDING_QUESTION_EVENT = "onbible:pending-question";

export const clearPendingQuestion = (): void => {
	try {
		localStorage.removeItem(KEY);
	} catch (error) {
		console.error("Failed to clear the pending question", describeError(error));
	}
};

/**
 * A question written before signing in. It has no owner — the guest has none — so a short life
 * keeps it from reaching the next person who signs in on a shared computer.
 */
export const savePendingQuestion = (q: PendingQuestion): void => {
	try {
		localStorage.setItem(KEY, JSON.stringify({ ...q, timestamp: Date.now() }));
	} catch (error) {
		console.error("Failed to keep the pending question", describeError(error));
	}
	window.dispatchEvent(new Event(PENDING_QUESTION_EVENT));
};

/** Reads without forgetting: the question stays until the server has it. Expired or damaged ones are dropped. */
export const peekPendingQuestion = (now: number = Date.now()): PendingQuestion | null => {
	let raw: string | null;
	try {
		raw = localStorage.getItem(KEY);
	} catch (error) {
		console.error("Failed to read the pending question", describeError(error));
		return null;
	}
	if (!raw) return null;
	try {
		const { text, verses, timestamp } = JSON.parse(raw);
		if (typeof text === "string" && text.trim() && now - timestamp <= PENDING_QUESTION_TTL_MS) {
			return { text, verses: Array.isArray(verses) ? verses : [] };
		}
	} catch {
		// Damaged — dropped below.
	}
	clearPendingQuestion();
	return null;
};
